import React, { useState, useEffect, useCallback } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const TeacherMarksSubmission = ({ onNavigate, userInfo }) => {
    const [isMobile, setIsMobile] = useState(false);
    const [assignments, setAssignments] = useState([]);
    const [assignmentsLoading, setAssignmentsLoading] = useState(true);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [students, setStudents] = useState([]);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // API helper
    const apiCall = useCallback(async (endpoint, options = {}) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) throw new Error('No auth token');
        const response = await fetch(`${API_LINK}/api/teacher${endpoint}`, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...options.headers },
            ...options
        });
        if (!response.ok) {
            if (response.status === 401) { sessionStorage.clear(); localStorage.clear(); if (onNavigate) onNavigate('home'); }
            throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
    }, [onNavigate]);

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth <= 1024);
        check(); window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    // Fetch assignments
    useEffect(() => {
        (async () => {
            setAssignmentsLoading(true);
            try {
                const result = await apiCall('/marks/my-assignments');
                if (result.success) setAssignments(result.data || []);
            } catch (err) { console.error(err); }
            setAssignmentsLoading(false);
        })();
    }, [apiCall]);

    // Fetch students when assignment selected
    useEffect(() => {
        if (!selectedAssignment) { setStudents([]); return; }
        (async () => {
            setStudentsLoading(true);
            setSubmitError(null);
            setSubmitSuccess(false);
            try {
                // Use batch + subject_name to fetch students via the attendance/students route
                const { batch, subject_name } = selectedAssignment;
                const result = await apiCall(
                    `/attendance/students?batch=${encodeURIComponent(batch)}&subject=${encodeURIComponent(subject_name || 'marks')}`
                );
                if (result.success && result.data) {
                    const list = Array.isArray(result.data) ? result.data : [];
                    setStudents(list.map(s => ({
                        id: s.student_id,
                        name: s.name,
                        rollNo: s.roll_number || s.student_id,
                        marks: ''
                    })));
                } else {
                    setStudents([]);
                }
            } catch (err) {
                console.error('Error fetching students:', err);
                setSubmitError('Failed to load students. Check backend logs.');
            }
            setStudentsLoading(false);
        })();

        // Update status to in_progress
        if (selectedAssignment.status === 'assigned') {
            apiCall('/marks/update-assignment-status', {
                method: 'POST',
                body: JSON.stringify({ assignment_id: selectedAssignment.id, status: 'in_progress' })
            }).catch(() => { });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAssignment?.id, apiCall]);

    // Handle marks change — just a single number per student
    const handleMarksChange = (studentId, value) => {
        const maxMarks = parseInt(selectedAssignment?.total_marks) || 100;
        if (value && (isNaN(value) || parseFloat(value) < 0 || parseFloat(value) > maxMarks)) return;
        setStudents(prev => prev.map(s => s.id === studentId ? { ...s, marks: value } : s));
    };

    // Submit marks
    const handleSubmit = async () => {
        if (!selectedAssignment) return;
        const hasMarks = students.some(s => s.marks !== '');
        if (!hasMarks) { setSubmitError('Please enter marks for at least one student.'); return; }

        setSubmitting(true);
        setSubmitError(null);
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const res = await fetch(`${API_LINK}/api/teacher/marks/submit-exam-marks`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    exam_schedule_id: selectedAssignment.exam_schedule_id,
                    assignment_id: selectedAssignment.id,
                    batch: selectedAssignment.batch,
                    department: selectedAssignment.department,
                    year: selectedAssignment.year,
                    marks_type: 'semester',
                    marks: students.map(s => ({
                        student_id: s.id,
                        theory: s.marks || '0',
                        practical: '0'
                    }))
                })
            });
            const data = await res.json();
            if (data.success) {
                setSubmitSuccess(true);
                alert('✅ Marks submitted successfully!');
                setTimeout(() => setSubmitSuccess(false), 6000);
                setAssignments(prev => prev.map(a => a.id === selectedAssignment.id ? { ...a, status: 'completed' } : a));
                setSelectedAssignment(prev => ({ ...prev, status: 'completed' }));
            } else {
                throw new Error(data.message || 'Failed to submit');
            }
        } catch (err) {
            console.error(err);
            setSubmitError(err.message || 'Failed to submit marks');
        }
        setSubmitting(false);
    };

    // ── Styles ──
    const cardStyle = {
        background: '#fff', borderRadius: '14px', padding: '1.5rem',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: '1.2rem'
    };
    const statusColor = (s) => s === 'completed' ? '#238548' : s === 'in_progress' ? '#f59e0b' : '#6366f1';
    const statusLabel = (s) => s === 'completed' ? '✅ Completed' : s === 'in_progress' ? '🔄 In Progress' : '📌 Assigned';

    return (
        <Layout role="teacher" onNavigate={onNavigate} activePage="teacher-marks-submission" userInfo={userInfo}>
            <style>{`
                .marks-num { width: 80px; padding: 10px; border: 2px solid #ddd; border-radius: 8px; text-align: center; font-size: 1rem; font-weight: 600; transition: all 0.3s; }
                .marks-num:focus { border-color: #238548; outline: none; box-shadow: 0 0 0 3px rgba(35,133,72,0.12); }
                @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
            `}</style>

            {/* Header */}
            <div style={cardStyle}>
                <div style={{
                    background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                    color: '#fff', padding: '1.5rem', borderRadius: '10px', textAlign: 'center'
                }}>
                    <h1 style={{ margin: '0 0 0.3rem 0', fontSize: '1.6rem' }}>✍️ Marks Entry Portal</h1>
                    <p style={{ margin: 0, fontSize: '0.95rem', opacity: 0.9 }}>Enter marks for your assigned subjects</p>
                </div>
            </div>

            {/* ── Assignment Cards ── */}
            {!selectedAssignment && (
                <div style={cardStyle}>
                    <h2 style={{ margin: '0 0 1rem 0', color: '#238548', fontSize: '1.1rem' }}>
                        📌 Your Assigned Subjects
                    </h2>
                    {assignmentsLoading ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                            <div style={{ width: 40, height: 40, border: '3px solid #e3f2fd', borderTop: '3px solid #238548', borderRadius: '50%', margin: '0 auto 1rem', animation: 'spin 1s linear infinite' }}></div>
                            Loading assignments...
                        </div>
                    ) : assignments.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📭</div>
                            <h3 style={{ color: '#666', margin: '0 0 0.5rem 0' }}>No Assignments Yet</h3>
                            <p style={{ fontSize: '0.9rem' }}>The admin hasn't assigned any subjects to you.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
                            {assignments.map((a, i) => (
                                <div key={i} onClick={() => setSelectedAssignment(a)} style={{
                                    background: a.status === 'completed' ? '#f0faf3' : a.status === 'in_progress' ? '#fffbf0' : '#f0f4ff',
                                    border: `2px solid ${a.status === 'completed' ? '#b7e4c7' : a.status === 'in_progress' ? '#fed7aa' : '#c7d2fe'}`,
                                    borderRadius: '12px', padding: '1.2rem', cursor: 'pointer', transition: 'all 0.2s', position: 'relative'
                                }}
                                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                                >
                                    <span style={{
                                        position: 'absolute', top: 10, right: 10, background: statusColor(a.status),
                                        color: '#fff', padding: '3px 10px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700
                                    }}>{statusLabel(a.status)}</span>
                                    <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#333', marginBottom: '0.25rem', paddingRight: 90 }}>
                                        {a.subject_name || 'Subject'}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.8rem' }}>{a.subject_code || '-'}</div>
                                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', fontSize: '0.73rem' }}>
                                        <span style={{ background: '#e9ecef', padding: '3px 9px', borderRadius: '6px', color: '#555' }}>🏫 {a.department}</span>
                                        <span style={{ background: '#e9ecef', padding: '3px 9px', borderRadius: '6px', color: '#555' }}>📅 Year {a.year}</span>
                                        <span style={{ background: '#e9ecef', padding: '3px 9px', borderRadius: '6px', color: '#555' }}>👥 {a.batch}</span>
                                        <span style={{ background: '#d4edda', padding: '3px 9px', borderRadius: '6px', color: '#238548', fontWeight: 600 }}>📊 Total: {a.total_marks || 100}</span>
                                    </div>
                                    <div style={{ marginTop: '0.8rem', paddingTop: '0.6rem', borderTop: '1px solid rgba(0,0,0,0.06)', color: '#238548', fontWeight: 600, fontSize: '0.82rem' }}>
                                        {a.status === 'completed' ? '📊 View / Edit Marks' : '✍️ Enter Marks'} →
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── Marks Entry ── */}
            {selectedAssignment && (
                <>
                    {/* Header bar */}
                    <div style={cardStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                            <button onClick={() => { setSelectedAssignment(null); setStudents([]); setSubmitError(null); setSubmitSuccess(false); }}
                                style={{ background: '#f0f0f0', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', color: '#555' }}>
                                ← Back
                            </button>
                            <div style={{ flex: 1 }}>
                                <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#333' }}>
                                    {selectedAssignment.subject_name}
                                    <span style={{ fontWeight: 400, color: '#888', marginLeft: 8, fontSize: '0.85rem' }}>({selectedAssignment.subject_code})</span>
                                </h2>
                                <div style={{ fontSize: '0.78rem', color: '#888', marginTop: 4 }}>
                                    {selectedAssignment.department} • Year {selectedAssignment.year} • {selectedAssignment.batch}
                                </div>
                            </div>
                            <span style={{
                                background: statusColor(selectedAssignment.status), color: '#fff',
                                padding: '4px 12px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700
                            }}>{statusLabel(selectedAssignment.status)}</span>
                        </div>
                    </div>

                    {/* Messages */}
                    {submitSuccess && (
                        <div style={{ background: '#d4edda', border: '1px solid #c3e6cb', color: '#155724', padding: '1rem', borderRadius: '10px', marginBottom: '1rem', fontWeight: 500 }}>
                            ✅ Marks submitted successfully!
                        </div>
                    )}
                    {submitError && (
                        <div style={{ background: '#f8d7da', border: '1px solid #f5c6cb', color: '#721c24', padding: '1rem', borderRadius: '10px', marginBottom: '1rem', fontWeight: 500 }}>
                            ⚠️ {submitError}
                        </div>
                    )}

                    {/* Students Table */}
                    <div style={cardStyle}>
                        {studentsLoading ? (
                            <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
                                <div style={{ width: 40, height: 40, border: '3px solid #e3f2fd', borderTop: '3px solid #238548', borderRadius: '50%', margin: '0 auto 1rem', animation: 'spin 1s linear infinite' }}></div>
                                Loading students...
                            </div>
                        ) : students.length > 0 ? (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                    <h3 style={{ margin: 0, fontSize: '1rem', color: '#333' }}>
                                        📝 Enter Marks <span style={{ fontWeight: 400, fontSize: '0.85rem', color: '#888' }}>({students.length} students • Max: {selectedAssignment.total_marks || 100})</span>
                                    </h3>
                                </div>

                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '2px solid #238548', fontSize: isMobile ? '0.85rem' : '0.95rem' }}>
                                        <thead>
                                            <tr style={{ background: '#238548', color: '#fff' }}>
                                                <th style={{ padding: '12px', border: '1px solid #238548', textAlign: 'left', width: 50 }}>S.No</th>
                                                <th style={{ padding: '12px', border: '1px solid #238548', textAlign: 'left' }}>Student ID</th>
                                                <th style={{ padding: '12px', border: '1px solid #238548', textAlign: 'left' }}>Student Name</th>
                                                <th style={{ padding: '12px', border: '1px solid #238548', textAlign: 'center', width: 120 }}>Marks ({selectedAssignment.total_marks || 100})</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {students.map((student, idx) => (
                                                <tr key={student.id} style={{ background: idx % 2 === 0 ? '#f8f9fa' : '#fff' }}>
                                                    <td style={{ padding: '10px 12px', border: '1px solid #ddd' }}>{idx + 1}</td>
                                                    <td style={{ padding: '10px 12px', border: '1px solid #ddd', fontWeight: 600, color: '#666', fontSize: '0.82rem' }}>{student.rollNo}</td>
                                                    <td style={{ padding: '10px 12px', border: '1px solid #ddd', fontWeight: 600 }}>{student.name}</td>
                                                    <td style={{ padding: '10px 12px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                        <input
                                                            type="text"
                                                            className="marks-num"
                                                            value={student.marks}
                                                            onChange={e => handleMarksChange(student.id, e.target.value)}
                                                            placeholder={`0-${selectedAssignment.total_marks || 100}`}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Submit button */}
                                <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
                                    <button onClick={handleSubmit} disabled={submitting} style={{
                                        background: submitting ? '#999' : 'linear-gradient(135deg, #238548, #2ea35e)',
                                        color: '#fff', border: 'none', padding: '12px 32px', borderRadius: '10px',
                                        cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '1rem', fontWeight: 700,
                                        boxShadow: submitting ? 'none' : '0 4px 12px rgba(35,133,72,0.3)',
                                        display: 'inline-flex', alignItems: 'center', gap: '0.5rem', transition: 'all 0.2s'
                                    }}>
                                        {submitting ? (
                                            <><div style={{ width: 18, height: 18, border: '2px solid #fff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div> Submitting...</>
                                        ) : '✅ Submit Marks'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>👥</div>
                                <p>No students found for batch <strong>{selectedAssignment.batch}</strong></p>
                            </div>
                        )}
                    </div>
                </>
            )}
        </Layout>
    );
};

export default TeacherMarksSubmission;
